import Vue from 'vue';

export default {
    namespaced: true,
    state: {
        comments: [],
        initialSetDone: false,
        article: {
            id: null,
        },
    },
    mutations: {
        /**
         * @param state {object}
         * @param comments {array}
         */
        setComments(state, comments) {
            state.comments = comments.reverse();
        },
        /**
         * @param state {object}
         * @param comment {object}
         */
        addComment(state, comment) {
            if (!state.initialSetDone) {
                state.initialSetDone = true;
            }
            state.comments = [comment, ...state.comments];
        },
        /**
         * @param state {object}
         * @param payload {object}
         */
        addChildComment(state, payload) {
            if (!state.initialSetDone) {
                state.initialSetDone = true;
            }
            const comment = state.comments.find((el) => el.id === payload.parentId);

            if (comment.children) {
                comment.children = [payload.comment, ...comment.children];
            } else {
                // A newly added comment returned from the api does not have a children property
                Vue.set(comment, 'children', [payload.comment]);
            }
        },
        /**
         * @param state {object}
         * @param id {number}
         */
        setArticleId(state, id) {
            state.article.id = id;
        },
        /**
         * @param state {object}
         * @param editedComment {object}
         */
        editComment(state, editedComment) {
            const comment = state.comments.find((el) => el.id === editedComment.id);
            comment.body = editedComment.body;
        },
        /**
         * @param state {object}
         * @param editedComment {object}
         * @param parentId {number}
         */
        editChildComment(state, { editedComment, parentId }) {
            const comment = state.comments.find((el) => el.id === parentId);
            const childComment = comment.children.find((el) => el.id === editedComment.id);

            if (childComment) {
                childComment.body = editedComment.body;
            } else {
                throw new Error(`Could not find child comment with id of ${editedComment.id}`);
            }
        },
        /**
         * @param state {object}
         * @param removedComment {object}
         */
        removeComment(state, removedComment) {
            const comment = state.comments.find((el) => el.id === removedComment.id);
            comment.body = removedComment.body;
            comment.user_full_name = removedComment.user_full_name;
            comment.user_avatar = removedComment.user_avatar;
            comment.status = removedComment.status;
            comment.user_id = null;
        },
        /**
         * @param state {object}
         * @param removedComment {object}
         * @param parentId {number}
         */
        removeChildComment(state, { removedComment, parentId }) {
            const comment = state.comments.find((el) => el.id === parentId);
            const childComment = comment.children.find((el) => el.id === removedComment.id);
            if (childComment) {
                childComment.body = removedComment.body;
                childComment.user_full_name = removedComment.user_full_name;
                childComment.user_avatar = removedComment.user_avatar;
                childComment.status = removedComment.status;
                childComment.user_id = null;
            } else {
                throw new Error(`Could not find child comment with id of ${removedComment.id}`);
            }
        },
        flagComment(state, commentId) {
            const comment = state.comments.find((el) => el.id === commentId);
            comment.status = 'Flagged';
            comment.is_flagged_by_user = true;
        },
    },
    actions: {
        /**
         * @param commit {function}
         * @param comments {array}
         */
        setComments({ commit }, comments) {
            commit('setComments', comments);
        },
        /**
         * @param commit {function}
         * @param comment {object}
         */
        addComment({ commit }, comment) {
            commit('addComment', comment);
        },
        /**
         * @param commit {function}
         * @param payload {object}
         */
        addChildComment({ commit }, payload) {
            commit('addChildComment', payload);
        },
        /**
         * @param commit {function}
         * @param id {number}
         */
        setArticleId({ commit }, id) {
            commit('setArticleId', id);
        },
        /**
         * @param commit {function}
         * @param comment {object}
         */
        editComment({ commit }, comment) {
            commit('editComment', comment);
        },
        /**
         * @param commit {function}
         * @param payload {object}
         */
        editChildComment({ commit }, payload) {
            commit('editChildComment', payload);
        },
        /**
         * @param commit {function}
         * @param commentId {object}
         */
        removeComment({ commit }, commentId) {
            return new Promise((resolve) => {
                axios.delete(`/api/comments/${commentId}`).then((response) => {
                    commit('removeComment', response.data.data);
                    resolve();
                });
            });
        },
        /**
         * @param commit {function}
         * @param commentId {number}
         * @param parentId {number}
         */
        removeChildComment({ commit }, { commentId, parentId }) {
            return new Promise((resolve) => {
                axios.delete(`/api/comments/${commentId}`).then((response) => {
                    commit('removeChildComment', {
                        removedComment: response.data.data,
                        parentId,
                    });
                    resolve();
                });
            });
        },
        flagComment({ commit }, commentId) {
            return new Promise((resolve) => {
                axios.post(`/api/comments/${commentId}/flag`).then(() => {
                    commit('flagComment', commentId);
                    resolve();
                });
            });
        },
    },
};
